import { useCallback, useEffect, useState } from 'react'

import { del, get, post, put } from './api_helper'
import { notifications } from './notifications'

export const useGetAll = (url: string): any[] => {
  const [data, setData] = useState([])

  useEffect(() => {
    get(url).then((res) => {
      setData(res.value)
    })
  }, [url])

  return data
}

export const UseGetBloqueosPromise = (url: string, body: any): any => {
  return post(url, body).then((json) => {
    return json
  })
}

interface IUseGetOnlyHook {
  dataDetalle: any
  obtenerDetalle: () => void
}

export const useGetOnly = (url: string): IUseGetOnlyHook => {
  const [dataDetalle, setDataDetalle] = useState([])

  // Memoizado por url: el efecto pasa a depender de la función en vez de la url, que es
  // exactamente lo mismo (la función solo cierra sobre url). Además el obtenerDetalle que
  // se devuelve al consumidor deja de cambiar en cada render.
  const obtenerDetalle = useCallback(async (): Promise<void> => {
    get(url).then((odata: any): void => {
      setDataDetalle(odata.value[0])
    })
  }, [url])

  useEffect(() => {
    obtenerDetalle()
  }, [obtenerDetalle])

  return { dataDetalle, obtenerDetalle }
}

export const UseGetOdata = async (url: string): Promise<any> => {
  return get(url)
    .then((odata) => {
      return odata.value
    })
    .catch(async (error) =>
      notifications({
        ex: error,
      }),
    )
}

export const addItem = (
  url: string,
  data: any,
  message: string,
  title: string,
  refresh: string,
): any => {
  return post(url, data)
    .then(async () => {
      const stateModal = false
      const dataRefresh = await UseGetOdata(refresh)

      notifications({ title: `${title}`, message: `${message}` })

      return { stateModal, dataRefresh }
    })
    .catch(async (error) => {
      notifications({ ex: error })
      const stateModal = false
      const dataRefresh = await UseGetOdata(refresh)

      return { stateModal, dataRefresh }
    })
}

/**
 * `true` si se guardó, `false` si no. Antes el `catch` era una arrow que devolvía el resultado de
 * `notifications()`, o sea `undefined`: funcionaba de casualidad —es falsy— pero nada lo decía, y el
 * tipo `any` dejaba que cualquiera lo cambiara sin enterarse.
 */
export const Update = (url: string, data: any): Promise<boolean> => {
  return put(`${url}`, data)
    .then(() => {
      notifications({
        title: 'Éxito',
        message: 'Se actualizó correctamente',
      })

      return true
    })
    .catch((error) => {
      notifications({ ex: error })

      return false
    })
}

/**
 * `true` si se borró, `false` si no.
 *
 * Devolvía `true` TAMBIÉN en el catch, y eso anulaba una guarda que sus llamadores sí tenían bien
 * escrita: `GridVehiculos`, `GridDocumentosVehiculo` y `GridDocumentosProveedor` hacen
 * `if (isRefresh) { setModalDelete(false); setRefresh(...) }`, o sea «cerrar y recargar solo si
 * funcionó». Con el true de mentira, un borrado fallido cerraba el diálogo y recargaba igual: el
 * usuario veía el aviso de error y la fila seguía ahí, sin saber si el sistema había hecho algo.
 *
 * Ahora, si falla, el diálogo se queda abierto para reintentar y no se recarga nada, porque nada
 * cambió.
 */
export const DeleteAll = (url: string, mensaje: string): Promise<boolean> => {
  return del(`${url}`)
    .then(() => {
      notifications({ message: `${mensaje}`, title: `Eliminado` })

      return true
    })
    .catch((error) => {
      notifications({ ex: error })

      return false
    })
}

/**
 * Borra y devuelve la lista recargada. A diferencia de las otras, acá recargar en el camino de error
 * es lo correcto y se deja a propósito: si el borrado falló, el ítem sigue existiendo y la recarga
 * muestra justamente eso. Sus dos llamadores hacen `setData(await DeleteItem(...))`, así que
 * devolver `null` al fallar les dejaría la grilla vacía, que sería mentir al revés.
 */
export const DeleteItem = (
  url: string,
  mensaje: string,
  refresh: string,
): any => {
  return del(`${url}`)
    .then(() => {
      notifications({ message: `${mensaje}`, title: `Eliminado` })

      return UseGetOdata(refresh)
    })
    .catch((error) => {
      notifications({ ex: error })

      return UseGetOdata(refresh)
    })
}

/**
 * `true` si se creó, `false` si no.
 *
 * Devolvía `true` también en el catch, y eso dejaba en código muerto la guarda del allotment masivo
 * (`AllotmentMasivoForm`): ahí hay un `if (!seGenero) return` puesto para que la ventana NO se cierre
 * cuando falla y no se pierda todo lo que el usuario tecleó. Con el true de mentira la ventana se
 * cerraba igual y las cantidades se perdían, que es exactamente el defecto que esa guarda venía a
 * resolver.
 */
export const addNewItem = (url: string, data: any): Promise<boolean> => {
  return post(url, data)
    .then(() => {
      notifications({
        title: 'Éxito',
        message: 'Se ingresó correctamente',
      })

      return true
    })
    .catch((error) => {
      notifications({ ex: error })

      return false
    })
}
